import { useEffect } from 'react';
import { setUser } from './store/reducers/user';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import * as Sentry from '@sentry/react';

// API
import { RequireAuth } from './components/RequireAuth';

// pages
import Home from './pages/home';
import NotFound from './pages/not-found';
import Login from './pages/auth/login';
import Forbidden from './pages/auth/Forbidden';
import Timetable from './pages/timetable';
import Events from './pages/events/events';
// import pages - manage
import TermPlanner from './pages/manage/term-planner';
import TermListing from './pages/manage/term-listing';
import SpecialMessages from './pages/manage/special-messages';
import Instructors from './pages/manage/instructors';
import OfficeHours from './pages/manage/office-hours';

// import pages - room
import RoomSearch from './pages/room/room-search';
import RoomConfiguration from './pages/room/room-configuration';
import RoomConflicts from './pages/room/room-conflicts';

// pages - reports
import DailyGrid from './pages/reports/daily-grid';
import GENRSList from './pages/reports/genrs-list';

// pages - downloads
import WeeklyReport from './pages/downloads/weekly-report';
import RoomBookingReport from './pages/downloads/room-booking-report';

// pages - battleship
import Battleship from './pages/battleship/battleship';

// pages - public site
import Terms from './pages/public-site/terms';
import Accounts from './pages/manage/accounts';
import Programs from './pages/public-site/programs';
import Exams from './pages/public-site/exams';

// comps
import MainLayout from './components/layout/mainLayout';
import { authenticationService } from './services/authenticationService.service';
import { useAppDispatch, useAppSelector } from './hooks/hooks';
import 'react-toastify/dist/ReactToastify.css';
import { setAppState, setViewType } from './store/reducers/app';
import { viewTypeStorageKey } from './constants';
import ItemList from './pages/events/item-list';
import Contact from "./pages/events/contact";
import EventGrid from "./pages/events/event-grid";

const App = (props: any) => {
    const dispatch = useAppDispatch();

    useEffect(() => {
        const subscription = authenticationService.currentUser.subscribe((user) => {
            // Account ID only - no name or BCIT ID
            Sentry.setUser(user ? { id: String(user.id) } : null);

            if (user) {
                dispatch(setUser(user));
                dispatch(setAppState(user));
                dispatch(setViewType(localStorage.getItem(viewTypeStorageKey)));
            }
        });

        // return unsubscribe method to execute when component unmounts
        // return subscription?.unsubscribe;
    }, []);

    return (
        <>
            <ToastContainer />
            <Router>
                <Routes>
                    <Route path={'/login'} element={<Login />} />
                    <Route path={'/forbidden'} element={<Forbidden />} />
                    <Route path={'/not-found'} element={<NotFound />} />
                    <Route element={<RequireAuth />}>
                        <Route element={<MainLayout />}>
                            <Route path="/" element={<Home />} />
                            <Route path="/timetable" element={<Timetable />} />
                            {/* ---------- Manage ----------- */}
                            <Route path="/term-planner" element={<TermPlanner />} />
                            <Route path="/term-listing" element={<TermListing />} />
                            <Route path="/office-hours" element={<OfficeHours />} />
                            <Route path="/special-messages" element={<SpecialMessages />} />
                            <Route path="/instructors" element={<Instructors />} />
                            {/* ---------- Event ----------- */}
                            <Route path="/item-list" element={<ItemList />} />
                            <Route path="/banner-event" element={<Events />} />
                            <Route path="/event-grid" element={<EventGrid />} />
                            <Route path="/contact" element={<Contact />} />
                            {/* ---------- Room ----------- */}
                            <Route path="/room-configuration" element={<RoomConfiguration />} />
                            <Route path="/room-search" element={<RoomSearch />} />
                            <Route path="/room-conflicts" element={<RoomConflicts />} />
                            {/* ---------- Reports ----------- */}
                            <Route path="/daily-grid" element={<DailyGrid />} />
                            <Route path="/genrs-list" element={<GENRSList />} />
                            {/* ---------- Downloads ----------- */}
                            <Route path="/weekly-report" element={<WeeklyReport />} />
                            <Route path="/room-booking-report" element={<RoomBookingReport />} />
                            {/* ---------- Battleship ----------- */}
                            <Route path="/battleship" element={<Battleship />} />
                            {/* ---------- Public Site ----------- */}
                            <Route path="/programs" element={<Programs />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/accounts" element={<Accounts />} />
                            <Route path="/exams" element={<Exams />} />
                        </Route>
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Router>
        </>
    );
};

export default App;
